import React from 'react';

import * as hubspot from '@/api/hubspot';

import * as analytics from '@/lib/analytics';
import { consumerToHOC } from '@/lib/hoc';

const Context = React.createContext({});

const { Provider, Consumer } = Context;

export const AnalyticsProvider = ({ children }) => {
  const [, setReady] = React.useState(() => false);

  // Sets ready state when analytics lib is loaded
  React.useEffect(() => {
    analytics.initialize(() => {
      setReady(true);
    });
  }, []);

  /**
   * LinkedIn conversion event - Tracks user login
   */
  const trackUserLoginLinkedInConversion = React.useCallback(
    /* eslint-disable-next-line camelcase */
    () => window.lintrk('track', { conversion_id: 10263754 }),
    [],
  );

  /**
   * LinkedIn conversion event - Tracks user reservation
   */
  const trackUserReservationLinkedInConversion = React.useCallback(
    /* eslint-disable-next-line camelcase */
    () => window.lintrk('track', { conversion_id: 10263762 }),
    [],
  );

  /**
   * Track a conversion via Google Analytics (gtag).
   */
  const trackGoogleConversion = React.useCallback((eventData) => {
    window.gtag('event', 'conversion', eventData);
  }, []);

  /**
   * Track a successful reservation event
   * with Google Tag Manager
   */
  const trackSuccessfulReservation = React.useCallback(
    (reservationType, reservationId) => {
      const type = {
        desk: 'desk',
        hourly: 'meeting_room',
        office: 'private_office',
      };

      if (!type[reservationType] || !reservationId) {
        return;
      }

      window.dataLayer.push({
        event: 'reservation_completed',
        reservation_type: type[reservationType],
        reservation_id: reservationId,
      });
    },
    [],
  );

  const context = {
    trackUserLoginLinkedInConversion,
    trackUserReservationLinkedInConversion,
    trackGoogleConversion,
    trackSuccessfulReservation,
    hubspot,
  };

  return <Provider value={context}>{children}</Provider>;
};

export default Context;
export const useAnaliticsContext = () => React.useContext(Context);

export const withAnalyticsContext = consumerToHOC(Consumer, 'analyticsContext');
